/**
 * iter235 · Manufacturing rule form — one editor for all six rule kinds.
 *
 * Every rule warns, never blocks (owner's choice), so the form has no
 * severity control. Product / material inputs are free text with datalist
 * suggestions so "strawberry" happily matches "Strawberry Seasoning".
 */
import React, { useState } from 'react';

const inputCls = 'w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-[12px] text-zinc-100 focus:outline-none focus:border-amber-500';

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const asText = (v) => (Array.isArray(v) ? v.join(', ') : (v ?? ''));

export default function RuleForm({ kind, seed = {}, products = [], materials = [], onCancel, onSave, saving }) {
  const p = seed.params || {};
  const [title, setTitle] = useState(seed.title || '');
  const [f, setF] = useState({
    before: asText(p.before), after: asText(p.after),
    same_line: p.same_line !== false, line_no: p.line_no || '',
    mode: p.mode || 'require_wash',
    from_products: asText(p.from_products), to_products: asText(p.to_products),
    consecutive_days: p.consecutive_days ?? 2,
    item_kind: p.item_kind || 'fg', item_name: p.item_name || '', min_kg: p.min_kg ?? '',
    material_name: p.material_name || '', lead_days: p.lead_days ?? 10,
    shift: p.shift || '', max_batches: p.max_batches ?? '', max_kg: p.max_kg ?? '',
    weekdays: p.weekdays || [], dates: asText(p.dates),
    note: p.note || '',
  });
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const params = () => {
    switch (kind) {
      case 'sequence': return { before: f.before, after: f.after, same_line: f.same_line, line_no: f.line_no };
      case 'wash': return {
        mode: f.mode, from_products: f.from_products, to_products: f.to_products,
        consecutive_days: f.consecutive_days, line_no: f.line_no, note: f.note,
      };
      case 'buffer': return { item_kind: f.item_kind, item_name: f.item_name, min_kg: f.min_kg || 0 };
      case 'procurement': return { material_name: f.material_name, lead_days: f.lead_days, note: f.note };
      case 'capacity': return { line_no: f.line_no, shift: f.shift, max_batches: f.max_batches || 0, max_kg: f.max_kg || 0 };
      default: return { weekdays: f.weekdays, dates: f.dates, note: f.note };
    }
  };

  const list = kind === 'buffer' ? (f.item_kind === 'rm' ? materials : products)
    : kind === 'procurement' ? materials : products;

  return (
    <div className="space-y-3" data-testid="fp-rule-form">
      <datalist id="fp-rule-names">{list.map(n => <option key={n} value={n} />)}</datalist>

      {kind === 'sequence' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F l="Run these FIRST" hint="comma separated · e.g. Strawberry">
            <input list="fp-rule-names" value={f.before} onChange={e => set('before', e.target.value)}
              data-testid="fp-rule-before" className={inputCls} placeholder="Strawberry" />
          </F>
          <F l="…BEFORE these" hint="e.g. Cream, Onion">
            <input list="fp-rule-names" value={f.after} onChange={e => set('after', e.target.value)}
              data-testid="fp-rule-after" className={inputCls} placeholder="Cream, Onion" />
          </F>
          <F l="Line (blank = every line)">
            <input value={f.line_no} onChange={e => set('line_no', e.target.value)} className={inputCls} placeholder="1" />
          </F>
          <label className="flex items-center gap-2 text-[12px] text-zinc-300 mt-5">
            <input type="checkbox" checked={f.same_line} onChange={e => set('same_line', e.target.checked)}
              data-testid="fp-rule-same-line" className="accent-amber-500" />
            Only when both run on the same line
          </label>
        </div>
      )}

      {kind === 'wash' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <F l="Rule type">
            <select value={f.mode} onChange={e => set('mode', e.target.value)} data-testid="fp-rule-wash-mode" className={inputCls}>
              <option value="require_wash" className="bg-zinc-900">Line MUST be washed in between</option>
              <option value="skip_wash" className="bg-zinc-900">No wash needed (same product back-to-back)</option>
            </select>
          </F>
          <F l="Line (blank = every line)">
            <input value={f.line_no} onChange={e => set('line_no', e.target.value)} className={inputCls} placeholder="1" />
          </F>
          {f.mode === 'require_wash' ? (
            <>
              <F l="After this product ran…" hint="blank = any product">
                <input list="fp-rule-names" value={f.from_products} onChange={e => set('from_products', e.target.value)}
                  data-testid="fp-rule-from" className={inputCls} placeholder="Garlic" />
              </F>
              <F l="…wash before making" hint="blank = anything else">
                <input list="fp-rule-names" value={f.to_products} onChange={e => set('to_products', e.target.value)}
                  data-testid="fp-rule-to" className={inputCls} placeholder="Chocolate" />
              </F>
            </>
          ) : (
            <>
              <F l="Product" hint="blank = any product repeating">
                <input list="fp-rule-names" value={f.from_products} onChange={e => set('from_products', e.target.value)}
                  data-testid="fp-rule-from" className={inputCls} placeholder="NMC" />
              </F>
              <F l="Runs back-to-back for (days)">
                <input type="number" min="2" value={f.consecutive_days} onChange={e => set('consecutive_days', e.target.value)}
                  data-testid="fp-rule-consecutive" className={inputCls} />
              </F>
            </>
          )}
          <div className="sm:col-span-2">
            <F l="Note for the shop floor">
              <input value={f.note} onChange={e => set('note', e.target.value)} className={inputCls}
                placeholder="e.g. dry clean only, no water" />
            </F>
          </div>
        </div>
      )}

      {kind === 'buffer' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <F l="Stock type">
            <select value={f.item_kind} onChange={e => set('item_kind', e.target.value)} data-testid="fp-rule-item-kind" className={inputCls}>
              <option value="fg" className="bg-zinc-900">Finished goods</option>
              <option value="rm" className="bg-zinc-900">Raw material</option>
            </select>
          </F>
          <F l="Item" hint="blank = every item of that type">
            <input list="fp-rule-names" value={f.item_name} onChange={e => set('item_name', e.target.value)}
              data-testid="fp-rule-item-name" className={inputCls} placeholder="blank = all" />
          </F>
          <F l="Minimum kg" hint={f.item_kind === 'fg' ? '0 = use the FG buffer limit' : '0 = use the reorder level'}>
            <input type="number" step="any" value={f.min_kg} onChange={e => set('min_kg', e.target.value)}
              data-testid="fp-rule-min-kg" className={inputCls} placeholder="0" />
          </F>
        </div>
      )}

      {kind === 'procurement' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <F l="Raw material" hint="blank = any short material">
            <input list="fp-rule-names" value={f.material_name} onChange={e => set('material_name', e.target.value)}
              data-testid="fp-rule-material" className={inputCls} placeholder="Paprika" />
          </F>
          <F l="Order this many days early">
            <input type="number" value={f.lead_days} onChange={e => set('lead_days', e.target.value)}
              data-testid="fp-rule-lead-days" className={inputCls} />
          </F>
          <F l="Note">
            <input value={f.note} onChange={e => set('note', e.target.value)} className={inputCls} placeholder="e.g. only from Vendor A" />
          </F>
        </div>
      )}

      {kind === 'capacity' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <F l="Line" hint="blank = every line">
            <input value={f.line_no} onChange={e => set('line_no', e.target.value)} data-testid="fp-rule-line" className={inputCls} placeholder="1" />
          </F>
          <F l="Shift">
            <select value={f.shift} onChange={e => set('shift', e.target.value)} className={inputCls}>
              <option value="" className="bg-zinc-900">Any shift</option>
              <option value="day" className="bg-zinc-900">Day</option>
              <option value="night" className="bg-zinc-900">Night</option>
            </select>
          </F>
          <F l="Max batches">
            <input type="number" value={f.max_batches} onChange={e => set('max_batches', e.target.value)}
              data-testid="fp-rule-max-batches" className={inputCls} placeholder="0 = no limit" />
          </F>
          <F l="Max kg">
            <input type="number" step="any" value={f.max_kg} onChange={e => set('max_kg', e.target.value)} className={inputCls} placeholder="0 = no limit" />
          </F>
        </div>
      )}

      {kind === 'calendar' && (
        <div className="space-y-3">
          <F l="Weekly off">
            <div className="flex flex-wrap gap-1.5" data-testid="fp-rule-weekdays">
              {WEEKDAYS.map(w => {
                const on = f.weekdays.includes(w);
                return (
                  <button key={w} type="button" data-testid={`fp-rule-weekday-${w}`}
                    onClick={() => set('weekdays', on ? f.weekdays.filter(x => x !== w) : [...f.weekdays, w])}
                    className={`text-[11px] px-2 py-1 rounded-full border capitalize ${on
                      ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
                      : 'bg-zinc-800 text-zinc-500 border-zinc-700'}`}>
                    {w.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </F>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F l="Specific holidays" hint="YYYY-MM-DD, comma separated">
              <input value={f.dates} onChange={e => set('dates', e.target.value)} data-testid="fp-rule-dates"
                className={inputCls} placeholder="2026-01-26, 2026-08-15" />
            </F>
            <F l="Note">
              <input value={f.note} onChange={e => set('note', e.target.value)} className={inputCls} placeholder="e.g. Diwali shutdown" />
            </F>
          </div>
        </div>
      )}

      <F l="Rule in your words" hint="leave blank and we write it for you">
        <input value={title} onChange={e => setTitle(e.target.value)} data-testid="fp-rule-title"
          className={inputCls} placeholder="e.g. Strawberry always before Cream" />
      </F>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button onClick={onCancel} data-testid="fp-rule-form-cancel"
          className="ti-btn-ghost text-[12px] px-3 py-1.5 rounded-lg">Cancel</button>
        <button onClick={() => onSave({ kind, title, params: params() })} disabled={saving}
          data-testid="fp-rule-form-save"
          className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-[12px] font-semibold px-3.5 py-1.5 rounded-lg">
          {saving ? 'Saving…' : 'Save rule'}
        </button>
      </div>
    </div>
  );
}

function F({ l, hint, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{l}</p>
      {children}
      {hint && <p className="text-[10px] text-zinc-600 mt-0.5">{hint}</p>}
    </div>
  );
}
